/**
 * ✨ Format utility functions for EVOP Inspector
 * Provides consistent number formatting and display utilities
 */

/**
 * 📊 Format numbers with 'k' notation for large values
 * 
 * @param value - The number to format
 * @returns Formatted string with 'k' notation for values over 999
 * 
 * @example
 * formatNumber(1234) // "1.2k"
 * formatNumber(999) // "999"
 * formatNumber(0) // "0"
 * formatNumber(5678) // "5.7k"
 */
export function formatNumber(value: number): string {
  if (value === 0) return '0'
  
  if (value >= 1000) {
    const thousands = value / 1000
    // Round to 1 decimal place and remove trailing zeros
    const formatted = (Math.round(thousands * 10) / 10).toString()
    return `${formatted}k`
  }
  
  return value.toString()
}

/**
 * 🎯 Format record counts with pluralization
 * 
 * @param count - Number of records
 * @param singular - Singular form of the noun (default: "record")
 * @param plural - Plural form of the noun (default: "records")
 * @returns Formatted string with proper pluralization
 * 
 * @example
 * formatRecordCount(1) // "1 record"
 * formatRecordCount(1234) // "1.2k records"
 * formatRecordCount(5, "sale", "sales") // "5 sales"
 */
export function formatRecordCount(
  count: number, 
  singular: string = "record", 
  plural: string = "records"
): string {
  const formattedCount = formatNumber(count)
  const noun = count === 1 ? singular : plural
  return `${formattedCount} ${noun}`
}